import { colors, layout } from './styles';
import { DocStep } from './types';

interface WelcomeScreenProps {
  steps: DocStep[];
  userName?: string;
  onStart: () => void;
}

const statusBadge = (status: DocStep['status'], stepNumber: number) => {
  if (status === 'completed') {
    return (
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          background: colors.success,
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 14,
          flexShrink: 0,
        }}
      >
        <i className="fas fa-check" />
      </div>
    );
  }
  if (status === 'partial') {
    return (
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          background: '#FCD34D',
          color: '#92400E',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 14,
          fontWeight: 700,
          flexShrink: 0,
        }}
      >
        <i className="fas fa-ellipsis-h" />
      </div>
    );
  }
  return (
    <div
      style={{
        width: 28,
        height: 28,
        borderRadius: '50%',
        background: '#E5E7EB',
        color: '#6B7280',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 13,
        fontWeight: 600,
        flexShrink: 0,
      }}
    >
      {stepNumber}
    </div>
  );
};

const roundToFive = (n: number, mode: 'floor' | 'ceil'): number => {
  const op = mode === 'floor' ? Math.floor : Math.ceil;
  return op(n / 5) * 5;
};

const estimateMinutes = (stepCount: number): string => {
  if (stepCount === 0) return '';
  const MAX = 35;
  const MIN = 5;
  let lower = roundToFive(stepCount * 2, 'floor');
  let upper = roundToFive(stepCount * 3, 'ceil');
  if (lower < MIN) lower = MIN;
  if (upper > MAX) upper = MAX;
  if (lower >= upper) lower = Math.max(MIN, upper - 5);
  if (lower >= upper) upper = lower + MIN;
  return `${lower}–${upper} minutes`;
};

const WelcomeScreen = ({ steps, userName, onStart }: WelcomeScreenProps) => {
  const visibleSteps = steps.filter((s) => s.kind !== 'REVIEW');
  const total = visibleSteps.length;
  const completed = visibleSteps.filter((s) => s.status === 'completed').length;
  const pendingCount = total - completed;
  const allDone = total > 0 && completed === total;
  const timeEstimate = estimateMinutes(pendingCount);
  const startLabel = allDone ? 'Review & Submit' : completed > 0 ? 'Continue' : 'Start Paperwork';

  return (
    <div style={layout.page}>
      <div style={{ ...layout.container, maxWidth: 720 }}>
        <div style={{ ...layout.card, textAlign: 'center', padding: '40px 32px' }}>
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              background: colors.accentSoft,
              margin: '0 auto 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 28,
              color: colors.accent,
            }}
          >
            <i className="fas fa-file-alt"></i>
          </div>
          <h1 style={{ fontSize: 26, fontWeight: 700, margin: 0 }}>Welcome to Your Onboarding</h1>
          {userName && (
            <div style={{ fontSize: 14, color: colors.text, marginTop: 6, fontWeight: 700 }}>{userName}</div>
          )}
          <p style={{ color: colors.muted, marginTop: 10, fontSize: 14 }}>
            Complete your employment paperwork to get started.
            {timeEstimate && ` This should take about ${timeEstimate}.`}
          </p>

          <button
            type="button"
            onClick={onStart}
            style={{ ...layout.primaryBtn, marginTop: 20, width: '100%', padding: 14 }}
          >
            {startLabel}
          </button>
          <div style={{ fontSize: 12, color: colors.muted, marginTop: 10 }}>
            Your progress will be saved automatically as you go
          </div>

          {total > 0 && (
            <div style={{ marginTop: 24, fontSize: 13, color: colors.muted }}>
              {completed} of {total} sections complete
            </div>
          )}

          <div style={{ marginTop: 14, textAlign: 'left' }}>
            {visibleSteps.map((step, idx) => (
              <div
                key={step.id}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  padding: '14px 16px',
                  background: '#F9FAFB',
                  border: `1px solid ${colors.border}`,
                  borderRadius: 10,
                  marginBottom: 10,
                }}
              >
                {statusBadge(step.status, idx + 1)}
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{step.title}</div>
                  <div style={{ fontSize: 13, color: colors.muted }}>{step.description}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WelcomeScreen;
